import {isNil} from 'ramda';

import {Base} from '../base/Base';
import type {ViewPartVariants} from '../CarDiagram.types';
import {FrontBumper} from '../parts/exterior/FrontBumper';
import {FrontLeftDoor} from '../parts/exterior/FrontLeftDoor';
import {FrontLeftHeadlight} from '../parts/exterior/FrontLeftHeadlight';
import {FrontLeftSidePanel} from '../parts/exterior/FrontLeftSidePanel';
import {FrontRightDoor} from '../parts/exterior/FrontRightDoor';
import {FrontRightHeadlight} from '../parts/exterior/FrontRightHeadlight';
import {FrontRightSidePanel} from '../parts/exterior/FrontRightSidePanel';
import {FrontWindshield} from '../parts/exterior/FrontWindshield';
import {Grille} from '../parts/exterior/Grille';
import {Hood} from '../parts/exterior/Hood';
import {RearBumper} from '../parts/exterior/RearBumper';
import {RearLeftDoor} from '../parts/exterior/RearLeftDoor';
import {RearLeftSidePanel} from '../parts/exterior/RearLeftSidePanel';
import {RearLeftTaillight} from '../parts/exterior/RearLeftTaillight';
import {RearRightDoor} from '../parts/exterior/RearRightDoor';
import {RearRightSidePanel} from '../parts/exterior/RearRightSidePanel';
import {RearRightTaillight} from '../parts/exterior/RearRightTaillight';
import {RearWindshield} from '../parts/exterior/RearWindshield';
import {RoofSunroof} from '../parts/exterior/RoofSunroof';
import {TowBar} from '../parts/exterior/TowBar';
import {Trunk} from '../parts/exterior/Trunk';
import {CenterConsole} from '../parts/interior/CenterConsole';
import {DashboardBottom} from '../parts/interior/DashboardBottom';
import {DashboardMiddleHeating} from '../parts/interior/DashboardMiddleHeating';
import {DashboardTop} from '../parts/interior/DashboardTop';
import {DriverSeat} from '../parts/interior/DriverSeat';
import {Floor} from '../parts/interior/Floor';
import {FrontLeftDoorInterior} from '../parts/interior/FrontLeftDoor';
import {FrontRightDoorInterior} from '../parts/interior/FrontRightDoor';
import {LeftMiddlePillar} from '../parts/interior/LeftMiddlePillar';
import {PassengerSeat} from '../parts/interior/PassengerSeat';
import {RearBoard} from '../parts/interior/RearBoard';
import {RearLeftDoorInterior} from '../parts/interior/RearLeftDoor';
import {RearLeftSidePanelInterior} from '../parts/interior/RearLeftSidePanel';
import {RearRightDoorInterior} from '../parts/interior/RearRightDoor';
import {RearRightSidePanelInterior} from '../parts/interior/RearRightSidePanel';
import {RearSeats} from '../parts/interior/RearSeats';
import {RightMiddlePillar} from '../parts/interior/RightMiddlePillar';
import {SteeringWheel} from '../parts/interior/SteeringWheel';
import {TrunkInterior} from '../parts/interior/Trunk';

interface ExteriorViewProps {
  readonly partVariants: ViewPartVariants;
  readonly onPartClick?: (partId: string) => void;
}

export function ExteriorView(props: ExteriorViewProps) {
  const onPartClick = props.onPartClick;
  const clickable = (partId: string): (() => void) | undefined => {
    if (isNil(onPartClick)) {
      return undefined;
    }
    return () => onPartClick(partId);
  };

  return (
    <div className="relative h-[502px] w-[1342px]">
      <Base />
      {/* INTERIOR PARTS - background */}
      <Floor />
      <DashboardBottom />
      <DashboardMiddleHeating />
      <SteeringWheel />
      <DashboardTop />
      <FrontLeftDoorInterior />
      <FrontRightDoorInterior />
      <RearLeftDoorInterior />
      <RearRightDoorInterior />
      <LeftMiddlePillar />
      <RightMiddlePillar />
      <RearLeftSidePanelInterior />
      <RearRightSidePanelInterior />
      <TrunkInterior />
      <RearBoard />
      <CenterConsole />
      <DriverSeat />
      <PassengerSeat />
      <RearSeats />
      {/* EXTERIOR SPECIFIC PARTS — order = largest body shapes first, smallest details last.
          Sibling SVGs are stacked in DOM order, so the last one wins hover at any pixel. */}
      <RoofSunroof variant={props.partVariants['roof'] ?? 'default'} onClick={clickable('roof')} />
      <FrontRightSidePanel
        variant={props.partVariants['front-right-side-panel'] ?? 'default'}
        onClick={clickable('front-right-side-panel')}
      />
      <FrontLeftSidePanel
        variant={props.partVariants['front-left-side-panel'] ?? 'default'}
        onClick={clickable('front-left-side-panel')}
      />
      <RearLeftSidePanel
        variant={props.partVariants['rear-left-side-panel'] ?? 'default'}
        onClick={clickable('rear-left-side-panel')}
      />
      <RearRightSidePanel
        variant={props.partVariants['rear-right-side-panel'] ?? 'default'}
        onClick={clickable('rear-right-side-panel')}
      />
      <FrontBumper
        variant={props.partVariants['front-bumper'] ?? 'default'}
        onClick={clickable('front-bumper')}
      />
      <RearBumper
        variant={props.partVariants['rear-bumper'] ?? 'default'}
        onClick={clickable('rear-bumper')}
      />
      <Hood variant={props.partVariants['hood'] ?? 'default'} onClick={clickable('hood')} />
      <Trunk variant={props.partVariants['trunk'] ?? 'default'} onClick={clickable('trunk')} />
      <FrontWindshield
        variant={props.partVariants['front-windshield'] ?? 'default'}
        onClick={clickable('front-windshield')}
      />
      <RearWindshield
        variant={props.partVariants['rear-windshield'] ?? 'default'}
        onClick={clickable('rear-windshield')}
      />
      <FrontLeftDoor
        variant={props.partVariants['front-left-door'] ?? 'default'}
        onClick={clickable('front-left-door')}
      />
      <FrontRightDoor
        variant={props.partVariants['front-right-door'] ?? 'default'}
        onClick={clickable('front-right-door')}
      />
      <RearLeftDoor
        variant={props.partVariants['rear-left-door'] ?? 'default'}
        onClick={clickable('rear-left-door')}
      />
      <RearRightDoor
        variant={props.partVariants['rear-right-door'] ?? 'default'}
        onClick={clickable('rear-right-door')}
      />
      <FrontRightHeadlight
        variant={props.partVariants['front-right-headlight'] ?? 'default'}
        onClick={clickable('front-right-headlight')}
      />
      <FrontLeftHeadlight
        variant={props.partVariants['front-left-headlight'] ?? 'default'}
        onClick={clickable('front-left-headlight')}
      />
      <RearRightTaillight
        variant={props.partVariants['rear-right-taillight'] ?? 'default'}
        onClick={clickable('rear-right-taillight')}
      />
      <RearLeftTaillight
        variant={props.partVariants['rear-left-taillight'] ?? 'default'}
        onClick={clickable('rear-left-taillight')}
      />
      <Grille variant={props.partVariants['grille'] ?? 'default'} onClick={clickable('grille')} />
      <TowBar variant={props.partVariants['tow-bar'] ?? 'default'} onClick={clickable('tow-bar')} />
    </div>
  );
}
